import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { esd_storage } from '../js/Domain';
import { useFilesApi } from '../js/adapter/FilesAdapter';
import { OrderDetailResItem, useOrderApi } from '../js/adapter/OrderAdapter';
import GlobalLoading from './components/GlobalLoading';
import './carDeliveryConfirmForm.css';

type CheckboxGroup = 'identify' | 'equipment' | 'otherRemark' | 'carOutward' | 'driveList' | 'other' | 'equipmentGift' | 'soldOutGift';
type SignatureType = 'sales' | 'customer';

type FormFields = {
    owner: string;
    phone: string;
    address: string;
    carModel: string;
    carYear: string;
    licensePlate: string;
    vin: string;
    keyCount: string;
    nfcKeyCount: string;
    remoteKeyCount: string;
    companyName: string;
    otherReason9: string;
    otherReason10: string;
    otherReason11: string;
    otherReason12: string;
    carMileage: string;
    equipmentGiftTexts: string[];
    soldOutGiftTexts: string[];
};

type SignatureState = {
    image: string;
    date: string;
};

type CarDeliveryOrderMain = {
    delivery_date?: string | null;
    orderer_customer_name?: string | null;
    potential_customer_name?: string | null;
    user_customer_name?: string | null;
    licenser_customer_name?: string | null;
    orderer_cellphone?: string | null;
    user_cellphone?: string | null;
    licenser_cellphone?: string | null;
    orderer_address_contact?: string | null;
    user_address_contact?: string | null;
    licenser_address_contact?: string | null;
    format_name?: string | null;
    car_type?: string | null;
    year?: number | null;
    license_plate?: string | null;
    vin_no?: string | null;
};

type CarDeliveryOrderDetail = OrderDetailResItem & {
    dt?: CarDeliveryOrderMain | null;
    dt_order_components?: Array<{
        component_name?: string | null;
    }> | null;
};

/**
 * 將簽名日期格式化為 yyyy/MM/dd。
 * @param date 要格式化的日期。
 * @returns 格式化後的日期字串。
 */
const formatSignDate = (date: Date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}/${month}/${day}`;
};

/**
 * 依目前執行環境組出舊版列印與檔案上傳服務的網域。
 * @returns 舊版 API 根路徑。
 */
const getLegacyApiDomain = () => {
    if (window.location.origin.includes('localhost') || window.location.origin.includes('127.0.0.1')) {
        return 'http://esd.genesys-tech.com/esd/';
    }
    if (window.location.origin.includes('test')) {
        return `${window.location.protocol}//${window.location.host}/esd/`;
    }
    return `${window.location.origin}/esd/`;
};

const CAR_IMAGE_MODELS = ['V90CC', 'XC90', 'XC60', 'XC40', 'S90', 'S60', 'V60'] as const;

/**
 * 將後端回傳的車型描述正規化為既有車圖檔名。
 * @param carType 後端回傳的車型字串。
 * @returns 命中的車圖檔名；若無對應圖檔則回傳空字串。
 */
const getCarImageModel = (carType?: string | null) => {
    const normalizedCarType = carType?.toUpperCase().replace(/[^A-Z0-9]/g, '') ?? '';
    if (!normalizedCarType) {
        return '';
    }

    return CAR_IMAGE_MODELS.find(model => normalizedCarType.includes(model.replace(/[^A-Z0-9]/g, ''))) ?? '';
};

/**
 * 交車證明單外部頁面，提供資料載入、勾選、簽名與 PDF 送出功能。
 * @returns 交車證明單 React 元件。
 */
const CarDeliveryConfirmForm = () => {
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const orderApiManager = useOrderApi();
    const filesApiManager = useFilesApi();
    const pageRef = useRef<HTMLDivElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const isDrawingRef = useRef(false);
    const lastPointRef = useRef<{ x: number; y: number } | null>(null);

    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const orderId = query.get('id') ?? '';
    const token = query.get('token') ?? String(esd_storage.getItem('token') ?? '');
    const assetBase = useMemo(() => getLegacyApiDomain().replace('/esd/', ''), []);

    const [isLoaded, setIsLoaded] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [orderDetail, setOrderDetail] = useState<CarDeliveryOrderDetail | null>(null);
    const [activeSignature, setActiveSignature] = useState<SignatureType | null>(null);
    const [uploadedPdfUrl, setUploadedPdfUrl] = useState('');
    const [formFields, setFormFields] = useState<FormFields>({
        owner: '',
        phone: '',
        address: '',
        carModel: '',
        carYear: '',
        licensePlate: '',
        vin: '',
        keyCount: '',
        nfcKeyCount: '',
        remoteKeyCount: '',
        companyName: '',
        otherReason9: '',
        otherReason10: '',
        otherReason11: '',
        otherReason12: '',
        carMileage: '',
        equipmentGiftTexts: ['', '', '', '', ''],
        soldOutGiftTexts: ['', '', '', '', ''],
    });
    const [checkedMap, setCheckedMap] = useState<Record<string, boolean>>({});
    const [signatures, setSignatures] = useState<Record<SignatureType, SignatureState>>({
        sales: { image: '', date: '' },
        customer: { image: '', date: '' },
    });

    const titleLogoSrc = `${assetBase}/PrintFile/Maintenance/caridentify_settings/icon/volvo-workmark.png`;
    const carType = orderDetail?.dt?.car_type ?? '';
    const carImageModel = getCarImageModel(carType);
    const carImageSrc = carImageModel
        ? `${assetBase}/PrintFile/Maintenance/caridentify_settings/car_image/${carImageModel}.jpg`
        : '';
    const isPageLoading = !errorMessage && (!isLoaded || isSubmitting);
    const loadingText = isSubmitting ? '檔案上傳中，請稍後' : '資料載入中，請稍後';

    useEffect(() => {
        if (token && esd_storage.getItem('token') !== token) {
            esd_storage.token = token;
        }
    }, [token]);

    useEffect(() => {
        const previousHtmlBackground = document.documentElement.style.background;
        const previousBodyBackground = document.body.style.background;
        const rootElement = document.getElementById('root');
        const previousRootBackground = rootElement?.style.background ?? '';

        document.documentElement.style.background = '#ffffff';
        document.body.style.background = '#ffffff';
        if (rootElement) {
            rootElement.style.background = '#ffffff';
        }

        return () => {
            document.documentElement.style.background = previousHtmlBackground;
            document.body.style.background = previousBodyBackground;
            if (rootElement) {
                rootElement.style.background = previousRootBackground;
            }
        };
    }, []);

    useEffect(() => {
        /**
         * 依訂單編號載入交車證明單所需的訂單明細與初始表單資料。
         */
        const loadData = async () => {
            try {
                if (!orderId) {
                    setErrorMessage('缺少訂單編號');
                    return;
                }

                if (!orderApiManager) {
                    return;
                }

                const orderDetailRes = await orderApiManager.OrderDetail({ order_id: orderId });
                if (!orderDetailRes.success || !orderDetailRes.data.dt) {
                    setErrorMessage(orderDetailRes.message || '查詢交車證明單資料失敗');
                    return;
                }

                const detail = orderDetailRes.data as CarDeliveryOrderDetail;
                const order = detail.dt;
                if (!order) {
                    setErrorMessage('查詢交車證明單資料失敗');
                    return;
                }

                setOrderDetail(detail);
                setFormFields(prev => ({
                    ...prev,
                    owner: order.licenser_customer_name || '',
                    phone: order.licenser_cellphone || order.orderer_cellphone || order.user_cellphone || '',
                    address: order.licenser_address_contact || order.orderer_address_contact || order.user_address_contact || '',
                    carModel: order.car_type || '',
                    carYear: order.year?.toString() || '',
                    licensePlate: order.license_plate || '',
                    vin: order.vin_no || '',
                    equipmentGiftTexts: (detail.dt_order_components ?? []).slice(0, 5).map(item => item.component_name || '').concat(['', '', '', '', '']).slice(0, 5),
                }));
                setIsLoaded(true);
            } catch (error) {
                setErrorMessage(error instanceof Error ? error.message : '查詢交車證明單資料失敗');
            }
        };

        loadData();
    }, [orderApiManager, orderId]);

    useEffect(() => {
        if (!activeSignature || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const dialog = canvas.parentElement;
        const width = dialog ? Math.min(dialog.clientWidth - 48, 672) : 672;
        canvas.width = width;
        canvas.height = 320;

        const context = canvas.getContext('2d');
        if (context) {
            context.lineCap = 'round';
            context.lineJoin = 'round';
            context.lineWidth = 3;
            context.strokeStyle = '#000000';
            context.clearRect(0, 0, canvas.width, canvas.height);
        }
    }, [activeSignature]);

    /**
     * 切換指定群組 checkbox 的勾選狀態。
     * @param group checkbox 所屬群組。
     * @param value checkbox 值。
     * @param index 同群組中的索引位置。
     */
    const toggleCheckbox = (group: CheckboxGroup, value: string, index: number) => {
        const key = `${group}:${value}:${index}`;
        setCheckedMap(prev => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    /**
     * 更新單一文字欄位值。
     * @param name 表單欄位名稱。
     * @param value 欄位新值。
     */
    const handleTextChange = (name: keyof FormFields, value: string) => {
        setFormFields(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    /**
     * 更新陣列型文字欄位中的指定項目。
     * @param name 陣列欄位名稱。
     * @param index 欲更新的項目索引。
     * @param value 欄位新值。
     */
    const handleArrayTextChange = (name: 'equipmentGiftTexts' | 'soldOutGiftTexts', index: number, value: string) => {
        setFormFields(prev => {
            const next = [...prev[name]];
            next[index] = value;
            return {
                ...prev,
                [name]: next,
            };
        });
    };

    /**
     * 將指標事件座標轉為簽名 canvas 的內部座標。
     * @param event Canvas pointer event。
     * @returns Canvas 座標；若 canvas 不存在則回傳 null。
     */
    const getCanvasPoint = (event: React.PointerEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        return {
            x: ((event.clientX - rect.left) / rect.width) * canvas.width,
            y: ((event.clientY - rect.top) / rect.height) * canvas.height,
        };
    };

    /**
     * 開始簽名繪製並記錄起始點。
     * @param event Canvas pointer event。
     */
    const handleSignStart = (event: React.PointerEvent<HTMLCanvasElement>) => {
        const point = getCanvasPoint(event);
        if (!point || !canvasRef.current) return;
        isDrawingRef.current = true;
        lastPointRef.current = point;
        canvasRef.current.setPointerCapture(event.pointerId);
    };

    /**
     * 依滑動軌跡在簽名 canvas 上連線繪製。
     * @param event Canvas pointer event。
     */
    const handleSignMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
        if (!isDrawingRef.current || !canvasRef.current) return;
        const point = getCanvasPoint(event);
        const context = canvasRef.current.getContext('2d');
        if (!point || !context || !lastPointRef.current) return;
        context.beginPath();
        context.moveTo(lastPointRef.current.x, lastPointRef.current.y);
        context.lineTo(point.x, point.y);
        context.stroke();
        lastPointRef.current = point;
    };

    /**
     * 結束簽名繪製狀態。
     */
    const handleSignEnd = () => {
        isDrawingRef.current = false;
        lastPointRef.current = null;
    };

    /**
     * 清除目前簽名 canvas 的內容。
     */
    const clearSignatureCanvas = () => {
        const canvas = canvasRef.current;
        const context = canvas?.getContext('2d');
        if (canvas && context) {
            context.clearRect(0, 0, canvas.width, canvas.height);
        }
    };

    /**
     * 將目前簽名 canvas 內容轉成圖片並寫回對應簽章欄位。
     */
    const saveSignature = () => {
        if (!activeSignature || !canvasRef.current) return;
        const image = canvasRef.current.toDataURL('image/png');
        setSignatures(prev => ({
            ...prev,
            [activeSignature]: {
                image,
                date: formatSignDate(new Date()),
            },
        }));
        setActiveSignature(null);
    };

    /**
     * 將原頁面表單狀態同步到列印用 DOM clone。
     * @param sourceRoot 原始頁面節點。
     * @param clonedRoot 複製後的頁面節點。
     */
    const syncFormStateToClone = (sourceRoot: HTMLElement, clonedRoot: HTMLElement) => {
        const sourceInputs = sourceRoot.querySelectorAll('input');
        const clonedInputs = clonedRoot.querySelectorAll('input');
        sourceInputs.forEach((sourceInput, index) => {
            const clonedInput = clonedInputs[index];
            if (!clonedInput) return;
            if (sourceInput.type === 'checkbox') {
                if (sourceInput.checked) clonedInput.setAttribute('checked', 'true');
                else clonedInput.removeAttribute('checked');
            } else {
                clonedInput.setAttribute('value', sourceInput.value);
            }
        });
    };

    /**
     * 產生上傳 PDF 所需的頁面 HTML 與頁首 HTML。
     * @returns 列印用 HTML 內容；若頁面尚未可用則回傳 null。
     */
    const buildPrintHtml = () => {
        if (!pageRef.current) return null;
        const clonedDocument = document.documentElement.cloneNode(true) as HTMLElement;
        syncFormStateToClone(document.documentElement, clonedDocument);

        const clonedBody = clonedDocument.querySelector('body') as HTMLBodyElement | null;
        const clonedRoot = clonedDocument.querySelector('#root') as HTMLElement | null;
        const clonedPage = clonedDocument.querySelector('.car-delivery-page');
        if (!clonedPage) return null;

        clonedPage.classList.add('print-mode');
        clonedDocument.style.height = 'auto';
        clonedDocument.style.overflow = 'visible';
        if (clonedBody) {
            clonedBody.style.height = 'auto';
            clonedBody.style.minHeight = '0';
            clonedBody.style.overflow = 'visible';
            clonedBody.style.margin = '0';
        }
        if (clonedRoot) {
            clonedRoot.style.height = 'auto';
            clonedRoot.style.minHeight = '0';
            clonedRoot.style.overflow = 'visible';
        }
        clonedPage.setAttribute('style', `${clonedPage.getAttribute('style') ?? ''};height:auto;min-height:0;overflow:visible;padding-bottom:0;`);

        const html = clonedDocument.outerHTML;
        const header = clonedDocument.querySelector('.header')?.outerHTML ?? '';
        const footer = clonedDocument.querySelector('.footer')?.outerHTML ?? '';
        const bodyContent = clonedDocument.querySelector('.bodyContent')?.outerHTML ?? '';

        let pdfHtml = html.replace(header, '').replace(footer, '');
        let headerHtml = html.replace(bodyContent, '').replace(footer, '');

        clonedDocument.querySelectorAll('.print_none, .show_none').forEach(item => {
            pdfHtml = pdfHtml.replace(item.outerHTML, '');
            headerHtml = headerHtml.replace(item.outerHTML, '');
        });

        return {
            pdfHtml: `<!DOCTYPE html>${pdfHtml}`,
            headerHtml: `<!DOCTYPE html>${headerHtml}`,
        };
    };

    /**
     * 將目前表單轉為 PDF HTML 並上傳，成功後提供 PDF 連結。
     */
    const handleSubmit = async () => {
        if (!token) {
            enqueueSnackbar('缺少授權資訊', { variant: 'warning' });
            return;
        }

        const printHtml = buildPrintHtml();
        if (!printHtml) {
            enqueueSnackbar('頁面轉檔失敗', { variant: 'warning' });
            return;
        }

        setIsSubmitting(true);
        try {
            if (!filesApiManager) {
                enqueueSnackbar('檔案服務尚未初始化', { variant: 'warning' });
                return;
            }

            const res = await filesApiManager.UploadHtmlPDF({
                belong: 'MeetingRoom',
                type: '交車證明單',
                id: orderId,
                pdf_html: printHtml.pdfHtml,
                header_html: printHtml.headerHtml,
                header_height: 78,
            });

            if (!res.success) {
                enqueueSnackbar(res.message || '檔案上傳失敗', { variant: 'warning' });
                return;
            }

            setUploadedPdfUrl(String(res.data ?? ''));
            enqueueSnackbar('已完成線上簽名', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar(error instanceof Error ? error.message : '檔案上傳失敗', { variant: 'warning' });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isLoaded && !errorMessage) {
        return <React.Fragment>
            <div className="car-delivery-page"></div>
            <GlobalLoading text={loadingText} />
        </React.Fragment>;
    }

    if (errorMessage) {
        return <div className="car-delivery-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: 18 }}>{errorMessage}</div>;
    }

    if (uploadedPdfUrl) {
        return <div className="car-delivery-success-page print_none">
            <div className="car-delivery-success-card">
                <p className="car-delivery-success-text">已完成線上簽名</p>
                <button type="button" className="car-delivery-success-button" onClick={() => window.open(uploadedPdfUrl, '_blank')}>查看 PDF</button>
            </div>
        </div>;
    }

    return <React.Fragment>
        {isPageLoading ? <GlobalLoading text={loadingText} /> : null}
        <div className="car-delivery-page" ref={pageRef}>
            {/* <div className="page-status print_none">
                <span className="page-status-text">訂單ID：{orderId}</span>
            </div> */}
            <header>
                <div className="row car-delivery-indentify header">
                    <div className="col-12 px-0">
                        <div className="row">
                            <div className="col-12 title-div">
                                <img className="title-logo" id="TitleLogo" src={titleLogoSrc} alt="title-logo" />
                            </div>
                            <div className="col-12 px-0">
                                <div className="row delivery-header-meta-row" style={{ padding: '0' }}>
                                    <div className="col-6">
                                        <label className="delivery-header-label">交車證明書</label>
                                    </div>
                                    <div className="col-6 text-right">
                                        <label className="delivery-header-label">交車日期：
                                            <span id="delivery_date" style={{ whiteSpace: 'pre' }}>        年        月        日</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>
            <table>
                <thead>
                    <tr>
                        <th>
                            <div className="header_space"></div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>
                            <div id="DeliveryFormBody" className="row car-delivery-indentify bodyContent">
                                <div className="col-12 px-0">
                                    <div className="row">

                                        {/** 基本資料 */}
                                        <div className="col-12 text-center bg-dark py-1">
                                            <span className="text-white">基本資料</span>
                                        </div>
                                        <div className="col-12 px-0 my-2">
                                            <div className="row content-div">
                                                <div className="content-child-div" style={{ height: 25, width: '40%' }}>
                                                    <span className="input-name">車主姓名</span>
                                                    <input className="input-text" id="owner" type="text" value={formFields.owner} onChange={e => handleTextChange('owner', e.target.value)} style={{ width: '75%' }} />
                                                </div>
                                                <div className="content-child-div" style={{ height: 25, width: '60%' }}>
                                                    <span className="input-name">聯絡電話</span>
                                                    <input className="input-text" id="phone" type="tel" value={formFields.phone} onChange={e => handleTextChange('phone', e.target.value)} style={{ position: 'absolute', right: 0, width: '50%' }} />
                                                </div>
                                                <div className="content-child-div" style={{ height: 25, width: '100%' }}>
                                                    <span className="input-name">地址</span>
                                                    <input className="input-text" id="address" type="text" value={formFields.address} onChange={e => handleTextChange('address', e.target.value)} style={{ position: 'absolute', width: '95%', right: 0 }} />
                                                </div>
                                                <div className="content-child-div" style={{ height: 25, width: '20%' }}>
                                                    <span className="input-name">車型</span>
                                                    <input className="input-text" id="carmodel" type="text" value={formFields.carModel} onChange={e => handleTextChange('carModel', e.target.value)} style={{ width: '70%' }} />
                                                </div>
                                                <div className="content-child-div" style={{ height: 25, width: '20%' }}>
                                                    <span className="input-name">年式</span>
                                                    <input className="input-text" id="caryear" type="text" value={formFields.carYear} onChange={e => handleTextChange('carYear', e.target.value)} style={{ width: '70%' }} />
                                                </div>
                                                <div className="content-child-div" style={{ height: 25, width: '30%' }}>
                                                    <span className="input-name">車牌號碼</span>
                                                    <input className="input-text" id="lpn" type="text" value={formFields.licensePlate} onChange={e => handleTextChange('licensePlate', e.target.value)} style={{ width: '70%' }} />
                                                </div>
                                                <div className="content-child-div" style={{ height: 25, width: '30%' }}>
                                                    <span className="input-name">車身號碼</span>
                                                    <input className="input-text" id="vin" type="text" value={formFields.vin} onChange={e => handleTextChange('vin', e.target.value)} style={{ position: 'absolute', width: '22%', right: 0 }} />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-6 px-0">

                                            {/** 交車證件資料袋 */}
                                            <div className="col-12 text-center bg-dark py-1" style={{ borderRight: '1px solid #FFF' }}>
                                                <span className="text-white">交車證件資料袋</span>
                                            </div>
                                            <div className="col-12 my-2 px-0">
                                                <div className="row content-div car-identifycard-div">
                                                    <div className="col-12 content-child-div">
                                                        <label className="check-label mr-3">客戶身分證
                                                            <span className="note-span"> ( 或公司營利事業登記證影本 )</span>
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['identify:customer_id_card:0'] ?? false} onChange={() => toggleCheckbox('identify', 'customer_id_card', 0)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-12 content-child-div">
                                                        <label className="check-label mr-3">客戶印章
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['identify:customer_seal:1'] ?? false} onChange={() => toggleCheckbox('identify', 'customer_seal', 1)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-12 content-child-div">
                                                        <label className="check-label mr-3">行車執照
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['identify:vehicle_license:2'] ?? false} onChange={() => toggleCheckbox('identify', 'vehicle_license', 2)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-12 content-child-div">
                                                        <label className="check-label mr-3">新領牌照登記書
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['identify:new_plate_registration:3'] ?? false} onChange={() => toggleCheckbox('identify', 'new_plate_registration', 3)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-12 content-child-div">
                                                        <label className="check-label mr-3">發票
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['identify:invoice:4'] ?? false} onChange={() => toggleCheckbox('identify', 'invoice', 4)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-12 content-child-div">
                                                        <label className="check-label mr-3">原廠出廠證明
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['identify:factory_certificate:5'] ?? false} onChange={() => toggleCheckbox('identify', 'factory_certificate', 5)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-12 content-child-div">
                                                        <label className="check-label mr-3">稅費收據
                                                            <span className="note-span"> ( 牌照稅、燃料稅、規費 )</span>
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['identify:tax_receipts:6'] ?? false} onChange={() => toggleCheckbox('identify', 'tax_receipts', 6)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-12 content-child-div">
                                                        <label className="check-label mr-3">保險單據
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['identify:insurance_documents:7'] ?? false} onChange={() => toggleCheckbox('identify', 'insurance_documents', 7)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-12 content-child-div">
                                                        <label className="check-label d-inline mr-3">鑰匙
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['identify:keys:8'] ?? false} onChange={() => toggleCheckbox('identify', 'keys', 8)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                        <input className="input-text key-count" id="key_count" type="number" value={formFields.keyCount} onChange={e => handleTextChange('keyCount', e.target.value)} />
                                                        <span style={{ fontSize: 14 }}>把</span>
                                                    </div>
                                                    <div className="col-12 content-child-div identify-key-row">
                                                        <label className="check-label d-inline mr-3 identify-key-label">
                                                            NFC 智慧卡片鑰匙
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['identify:nfc_smart_key_card:9'] ?? false} onChange={() => toggleCheckbox('identify', 'nfc_smart_key_card', 9)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                        <input className="input-text key-count identify-key-count" type="number" value={formFields.nfcKeyCount} onChange={e => handleTextChange('nfcKeyCount', e.target.value)} />
                                                        <span style={{ fontSize: 14 }}>張</span>
                                                        <span className="note-span identify-key-note">( 適用於 EX30 )</span>
                                                    </div>
                                                    <div className="col-12 content-child-div identify-key-row">
                                                        <label className="check-label d-inline mr-3 identify-key-label">
                                                            運動鑰匙
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['identify:sport_key:10'] ?? false} onChange={() => toggleCheckbox('identify', 'sport_key', 10)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                        <input className="input-text key-count identify-key-count" type="number" value={formFields.remoteKeyCount} onChange={e => handleTextChange('remoteKeyCount', e.target.value)} />
                                                        <span style={{ fontSize: 14 }}>把</span>
                                                        <span className="note-span identify-key-note">( 適用於 EX30 )</span>
                                                    </div>
                                                    <div className="col-12 content-child-div">
                                                        <label className="check-label mr-3">銷售主管、銷售顧問、服務廠接待人員名片
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['identify:staff_business_cards:11'] ?? false} onChange={() => toggleCheckbox('identify', 'staff_business_cards', 11)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-12 content-child-div">
                                                        <label className="check-label mr-3">車主尊榮救援服務手冊
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['identify:roadside_assistance_manual:12'] ?? false} onChange={() => toggleCheckbox('identify', 'roadside_assistance_manual', 12)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-12 content-child-div">
                                                        <label className="check-label mr-3">車主保固暨服務手冊
                                                            <span className="note-span"> ( 新車5,000 公里免費服務、保固範圍、保固時效、定期保養 )</span>
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['identify:warranty_and_service_manual:13'] ?? false} onChange={() => toggleCheckbox('identify', 'warranty_and_service_manual', 13)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-12 content-child-div">
                                                        <label className="check-label mr-3">車主手冊補充資料
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['identify:owner_manual_supplement:14'] ?? false} onChange={() => toggleCheckbox('identify', 'owner_manual_supplement', 14)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-12 content-child-div">
                                                        <label className="check-label mr-3">保固手冊單張小卡
                                                            <span className="note-span"> ( Recharge 車款 )</span>
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['identify:recharge_warranty_card:15'] ?? false} onChange={() => toggleCheckbox('identify', 'recharge_warranty_card', 15)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-12 content-child-div">
                                                        <label className="check-label mr-3">VOLVO 雙鏡DVR 使用手冊
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['identify:dvr_manual:16'] ?? false} onChange={() => toggleCheckbox('identify', 'dvr_manual', 16)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-12 content-child-div">
                                                        <label className="check-label mr-3">隔熱紙保固卡
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['identify:window_tint_warranty_card:17'] ?? false} onChange={() => toggleCheckbox('identify', 'window_tint_warranty_card', 17)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-5 content-child-div">
                                                        <label className="check-label d-inline" style={{ width: 'auto' }}>延長保固發票 /
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['identify:extended_warranty_invoice:18'] ?? false} onChange={() => toggleCheckbox('identify', 'extended_warranty_invoice', 18)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-4 content-child-div">
                                                        <label className="check-label d-inline mr-3">未購買
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['identify:not_purchased:19'] ?? false} onChange={() => toggleCheckbox('identify', 'not_purchased', 19)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="content-child-div" style={{ width: '95%', border: '1px solid', textAlign: 'center' }}>
                                                        <label style={{ fontSize: 13, marginBottom: 0 }}> * 自2020年2月起，強制汽車責任保險之保險證已全面電子化</label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/** 隨車配件點交 */}
                                        <div className="col-6 px-0">
                                            <div className="col-12 text-center bg-dark py-1">
                                                <span className="text-white">隨車配件點交</span>
                                            </div>
                                            <div className="col-12 my-2 px-0">
                                                <div className="row content-div car-equipmen-check">
                                                    <div className="col-7 content-child-div">
                                                        <label className="check-label mr-3">高壓鋰電池滿電
                                                            <span className="note-span"> ( Recharge車款 )</span>
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['equipment:battery_fully_charged:0'] ?? false} onChange={() => toggleCheckbox('equipment', 'battery_fully_charged', 0)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-5 content-child-div">
                                                        <label className="check-label mr-3">警示三角架
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['equipment:warning_triangle:1'] ?? false} onChange={() => toggleCheckbox('equipment', 'warning_triangle', 1)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-7 content-child-div">
                                                        <label className="check-label mr-3">充電電線
                                                            <span className="note-span"> ( Recharge車款 )</span>
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['equipment:charging_cable:2'] ?? false} onChange={() => toggleCheckbox('equipment', 'charging_cable', 2)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-5 content-child-div">
                                                        <label className="check-label mr-3">補胎劑
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['equipment:tire_repair_sealant:3'] ?? false} onChange={() => toggleCheckbox('equipment', 'tire_repair_sealant', 3)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-7 content-child-div">
                                                        <label className="check-label mr-3">油箱 3/4 以上
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['equipment:fuel_above_three_quarters:4'] ?? false} onChange={() => toggleCheckbox('equipment', 'fuel_above_three_quarters', 4)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-5 content-child-div">
                                                        <label className="check-label mr-3">打氣機
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['equipment:air_pump:5'] ?? false} onChange={() => toggleCheckbox('equipment', 'air_pump', 5)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-7 content-child-div">
                                                        <label className="check-label mr-3">急救包
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['equipment:first_aid_kit:6'] ?? false} onChange={() => toggleCheckbox('equipment', 'first_aid_kit', 6)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-5 content-child-div">
                                                        <label className="check-label mr-3">加油管
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['equipment:fuel_funnel:7'] ?? false} onChange={() => toggleCheckbox('equipment', 'fuel_funnel', 7)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-7 content-child-div">
                                                        <label className="check-label mr-3">反光背心
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['equipment:reflective_vest:8'] ?? false} onChange={() => toggleCheckbox('equipment', 'reflective_vest', 8)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                    <div className="col-5 content-child-div">
                                                        <label className="check-label mr-3">輪胎螺絲蓋夾
                                                            <input type="checkbox" className="checkbox-form" checked={checkedMap['equipment:wheel_bolt_cap_clip:9'] ?? false} onChange={() => toggleCheckbox('equipment', 'wheel_bolt_cap_clip', 9)} />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>

                                            {/** 車輛選購 / 贈送配備 */}
                                            <div className="col-12 text-center bg-dark py-1 mt-4">
                                                <span className="text-white">車輛選購 / 贈送配備</span>
                                            </div>
                                            <div className="col-12 my-2 px-0">
                                                <div className="row content-div equipmentgift-div">
                                                    {formFields.equipmentGiftTexts.map((value, index) => (
                                                        <div className="col-12 content-child-div" key={`equipmentgift-${index}`}>
                                                            <label className="check-label mr-3">
                                                                <input type="checkbox" className="checkbox-form" checked={checkedMap[`equipmentGift:${index}:${index}`] ?? false} onChange={() => toggleCheckbox('equipmentGift', `${index}`, index)} />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                            <input className="input-text" type="text" value={value} onChange={e => handleArrayTextChange('equipmentGiftTexts', index, e.target.value)} />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/** 交車禮 */}
                                            <div className="col-12 text-center bg-dark py-1 mt-4">
                                                <span className="text-white">交車禮</span>
                                            </div>
                                            <div className="col-12 my-2 px-0">
                                                <div className="row content-div soldoutgift-div">
                                                    {formFields.soldOutGiftTexts.map((value, index) => (
                                                        <div className="col-12 content-child-div" key={`soldoutgift-${index}`}>
                                                            <label className="check-label mr-3">
                                                                <input type="checkbox" className="checkbox-form" checked={checkedMap[`soldOutGift:${index}:${index}`] ?? false} onChange={() => toggleCheckbox('soldOutGift', `${index}`, index)} />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                            <input className="input-text" type="text" value={value} onChange={e => handleArrayTextChange('soldOutGiftTexts', index, e.target.value)} />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-12 px-0 print-page-block print-page-break-before">
                                            <div className="row">
                                                <div className="col-12 text-center bg-dark py-1">
                                                    <span className="text-white">其他說明事項</span>
                                                </div>
                                                <div className="col-12 my-2 px-0">
                                                    <div className="row content-div other-remark-div">
                                                        <div className="col-12 content-child-div">
                                                            <label className="check-label mr-3">車輛使用及保養須知，需請客戶務必閱讀服務手冊及保養手冊。
                                                                <input type="checkbox" className="checkbox-form" checked={checkedMap['otherRemark:maintenance_and_service_notice:0'] ?? false} onChange={() => toggleCheckbox('otherRemark', 'maintenance_and_service_notice', 0)} />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                        </div>
                                                        <div className="col-12 content-child-div">
                                                            <label className="check-label mr-3">車輛倘非經合格專案服務廠或合格技師依原廠技術規範進行任何維修/保養作業或改裝，或自行安裝不符合原廠規範的零組件，致車輛發生故障或損壞者，原廠製造商不予承擔保固責任。
                                                                <input type="checkbox" className="checkbox-form" checked={checkedMap['otherRemark:non_authorized_repair_notice:1'] ?? false} onChange={() => toggleCheckbox('otherRemark', 'non_authorized_repair_notice', 1)} />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                        </div>
                                                        <div className="col-12 content-child-div">
                                                            <label className="check-label mr-3">下載第三方修改原廠軟體及第三方軟體將造成車輛後續無法進行軟體更新及保固權益終止。
                                                                <input type="checkbox" className="checkbox-form" checked={checkedMap['otherRemark:third_party_software_notice:2'] ?? false} onChange={() => toggleCheckbox('otherRemark', 'third_party_software_notice', 2)} />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                        </div>
                                                        <div className="col-12 content-child-div">
                                                            <label className="check-label mr-3">油耗說明：「油耗測試值系在實驗室內，依規定的行車狀態於車體動力計上測得。實際道路行駛時，因受天候、路況、載重、使用空調系統、駕駛習慣及車輛維護保養等因素影響，其實際油耗值常低於測試值。」與能源效率標示圖樣貼紙燃油效率提昇方式說明。
                                                                <input type="checkbox" className="checkbox-form" checked={checkedMap['otherRemark:fuel_consumption_notice:3'] ?? false} onChange={() => toggleCheckbox('otherRemark', 'fuel_consumption_notice', 3)} />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                        </div>
                                                        <div className="col-4 content-child-div">
                                                            <label className="check-label mr-3">客戶服務專線
                                                                <span className="note-span"> ( 0800-40-60-80 )</span>
                                                                <input type="checkbox" className="checkbox-form" checked={checkedMap['otherRemark:customer_service_hotline:4'] ?? false} onChange={() => toggleCheckbox('otherRemark', 'customer_service_hotline', 4)} />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                        </div>
                                                        <div className="col-4 content-child-div">
                                                            <label className="check-label mr-3">服務廠人員、服務時間及設施介紹
                                                                <input type="checkbox" className="checkbox-form" checked={checkedMap['otherRemark:service_center_intro:5'] ?? false} onChange={() => toggleCheckbox('otherRemark', 'service_center_intro', 5)} />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                        </div>
                                                        <div className="col-4 content-child-div">
                                                            <label className="check-label mr-3">客戶滿意度調查說明
                                                                <span className="note-span"> ( 電訪、E-mail問卷 )</span>
                                                                <input type="checkbox" className="checkbox-form" checked={checkedMap['otherRemark:customer_satisfaction_survey:6'] ?? false} onChange={() => toggleCheckbox('otherRemark', 'customer_satisfaction_survey', 6)} />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                        </div>
                                                        <div className="col-12 content-child-div">
                                                            <label className="check-label mr-3">App下載：MY VOLVO DVR、Volvo Cars、Volvo EX30
                                                                <span className="note-span"> ( 適用於EX30 )。</span>
                                                                <input type="checkbox" className="checkbox-form" checked={checkedMap['otherRemark:app_download_guide:7'] ?? false} onChange={() => toggleCheckbox('otherRemark', 'app_download_guide', 7)} />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                        </div>
                                                        <div className="col-12 content-child-div other-remark-reason-block">
                                                            <label className="check-label mr-3 other-remark-reason-label">
                                                                協助綁定 Volvo Car Taiwan Line 官方帳號之車主身分、車主專區功能說明。
                                                                <input type="checkbox" className="checkbox-form" checked={checkedMap['otherRemark:line_official_account_binding:8'] ?? false} onChange={() => toggleCheckbox('otherRemark', 'line_official_account_binding', 8)} />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                            <div className="other-remark-reason-row">
                                                                <span className="other-remark-reason-prefix">如未完成，原因</span>
                                                                <input className="input-text other-reason" type="text" value={formFields.otherReason9} onChange={e => handleTextChange('otherReason9', e.target.value)} />
                                                            </div>
                                                        </div>
                                                        <div className="col-12 content-child-div other-remark-reason-block">
                                                            <label className="check-label mr-3 other-remark-reason-label">
                                                                提供最新版 EX30 APP 設定及數位鑰匙設定使用指南( 彩色列印 )，協助客戶進行手機數位鑰匙設定。
                                                                <input type="checkbox" className="checkbox-form" checked={checkedMap['otherRemark:ex30_app_and_digital_key_setup:9'] ?? false} onChange={() => toggleCheckbox('otherRemark', 'ex30_app_and_digital_key_setup', 9)} />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                            <div className="other-remark-reason-row">
                                                                <span className="other-remark-reason-prefix">如未完成，原因</span>
                                                                <input className="input-text other-reason" type="text" value={formFields.otherReason10} onChange={e => handleTextChange('otherReason10', e.target.value)} />
                                                            </div>
                                                        </div>
                                                        <div className="col-12 content-child-div other-remark-reason-block">
                                                            <label className="check-label mr-3 other-remark-reason-label">
                                                                提供最新版 Google 車載娛樂系統操作指南( 彩色列印 )，Google 車載系統車主設定與手機 Volvo Cars App 配對。
                                                                <input type="checkbox" className="checkbox-form" checked={checkedMap['otherRemark:google_infotainment_setup:10'] ?? false} onChange={() => toggleCheckbox('otherRemark', 'google_infotainment_setup', 10)} />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                            <div className="other-remark-reason-row">
                                                                <span className="other-remark-reason-prefix">如未完成，原因</span>
                                                                <input className="input-text other-reason" type="text" value={formFields.otherReason11} onChange={e => handleTextChange('otherReason11', e.target.value)} />
                                                            </div>
                                                        </div>
                                                        <div className="col-12 content-child-div other-remark-reason-block">
                                                            <label className="check-label mr-3 other-remark-reason-label">
                                                                Volvo on call 功能說明，協助客戶進行 Welcome Call，與 Call Center 完成車主資料核對。
                                                                <input type="checkbox" className="checkbox-form" checked={checkedMap['otherRemark:volvo_on_call_welcome_call:11'] ?? false} onChange={() => toggleCheckbox('otherRemark', 'volvo_on_call_welcome_call', 11)} />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                            <div className="other-remark-reason-row">
                                                                <span className="other-remark-reason-prefix">如未完成，原因</span>
                                                                <input className="input-text other-reason" type="text" value={formFields.otherReason12} onChange={e => handleTextChange('otherReason12', e.target.value)} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-12 px-0 important-remark-div">
                                                    <ul>
                                                        <li>本交車證明書，經客戶簽名後，即視為已依約完全給付上述點交事項，並由客戶點收無誤。本交車證明書經雙方簽認後交付副本乙份予客戶收執，爾後不再補發。</li>
                                                        <li>本車所有相關配件概依訂購合約書及本交車證明書記載為準，銷售顧問於本證明書或其他文件上約定非屬訂購合約書及交車證明書內容以外承認事項概與
                                                            <input className="input-text remark-company" id="company_name" type="text" value={formFields.companyName} onChange={e => handleTextChange('companyName', e.target.value)} />
                                                            無涉。
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/** 車輛操作說明 */}
                                <div className="col-12 px-0 print-page-block print-page-break-before">
                                    <div className="row">
                                        <div className="col-12 text-center bg-dark py-1">
                                            <span className="text-white">車輛操作說明</span>
                                        </div>
                                        <div className="col-12 my-2 px-0">
                                            <div className="row content-div car-outward-div">
                                                <div className="col-12 content-child-div">
                                                    <label className="check-label mr-3">
                                                        <span style={{ fontWeight: 'bold' }}>車身外觀　<span className="note-span">引擎蓋開啟操作、油箱蓋操作 / 位置說明、行李箱蓋操作、油品種類說明</span></span>
                                                        <input type="checkbox" className="checkbox-form" checked={checkedMap['carOutward:exterior_walkthrough:0'] ?? false} onChange={() => toggleCheckbox('carOutward', 'exterior_walkthrough', 0)} />
                                                        <span className="checkmark"></span>
                                                    </label>
                                                </div>
                                                <div className="col-12 content-child-div">
                                                    {carImageSrc ? <img src={carImageSrc} id="carmodel_image" alt="car-model" /> : null}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-12 my-2 px-0">
                                            <div className="row content-div car-driverset-div">
                                                <div className="col-4 content-child-div">
                                                    <label className="check-label mr-3">
                                                        <span style={{ fontWeight: 'bold' }}>中控台</span><br />
                                                        <div style={{ fontSize: 13 }}>
                                                            中控螢幕、Google 車載系統（地圖、語音<br />
                                                            助理、Play商店）、空調系統、警示燈、除霧、
                                                            音響系統操作、引擎啟動操作、行駛模式切換
                                                            <span className="note-span">（含排檔/B 檔操作）、電子手煞車、Auto</span>
                                                            Hold 操作說明、GPS、駐車輔助影像功能
                                                            操作說明、Carplay、藍芽設定
                                                        </div>
                                                        <input type="checkbox" className="checkbox-form" checked={checkedMap['driveList:center_console:0'] ?? false} onChange={() => toggleCheckbox('driveList', 'center_console', 0)} />
                                                        <span className="checkmark"></span>
                                                    </label>
                                                </div>
                                                <div className="col-4 content-child-div">
                                                    <div className="row">
                                                        <div className="col-12 content-child-div">
                                                            <label className="check-label mr-3">
                                                                <span style={{ fontWeight: 'bold' }}>多功能方向盤</span><br />
                                                                <div style={{ fontSize: 13 }}>PA II、ACC操作、雨刷、喇叭功能</div>
                                                                <input type="checkbox" className="checkbox-form" checked={checkedMap['driveList:multifunction_steering_wheel:1'] ?? false} onChange={() => toggleCheckbox('driveList', 'multifunction_steering_wheel', 1)} />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                        </div>
                                                        <div className="col-12 content-child-div">
                                                            <label className="check-label mr-3">
                                                                <span style={{ fontWeight: 'bold' }}>後視鏡操作</span><br />
                                                                <input type="checkbox" className="checkbox-form" checked={checkedMap['driveList:rearview_mirror_controls:2'] ?? false} onChange={() => toggleCheckbox('driveList', 'rearview_mirror_controls', 2)} />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                        </div>
                                                        <div className="col-12 content-child-div">
                                                            <label className="check-label mr-3">
                                                                <span style={{ fontWeight: 'bold' }}>儀錶板燈號說明</span><br />
                                                                <input type="checkbox" className="checkbox-form" checked={checkedMap['driveList:dashboard_indicator_intro:3'] ?? false} onChange={() => toggleCheckbox('driveList', 'dashboard_indicator_intro', 3)} />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                        </div>
                                                        <div className="col-12 content-child-div">
                                                            <label className="check-label mr-3">
                                                                <span style={{ fontWeight: 'bold' }}>燈光照明系統說明</span><br />
                                                                <input type="checkbox" className="checkbox-form" checked={checkedMap['driveList:lighting_system_intro:4'] ?? false} onChange={() => toggleCheckbox('driveList', 'lighting_system_intro', 4)} />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-4 content-child-div">
                                                    <div className="row">
                                                        <div className="col-12 content-child-div">
                                                            <label className="check-label mr-3">
                                                                <span style={{ fontWeight: 'bold' }}>電動座椅、中央門鎖及兒童安全鎖操作說明</span><br />
                                                                <input type="checkbox" className="checkbox-form" checked={checkedMap['driveList:power_seats_and_locks:5'] ?? false} onChange={() => toggleCheckbox('driveList', 'power_seats_and_locks', 5)} />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                        </div>
                                                        <div className="col-12 content-child-div">
                                                            <label className="check-label mr-3">
                                                                <span style={{ fontWeight: 'bold' }}>電動天窗操作</span><br />
                                                                <input type="checkbox" className="checkbox-form" checked={checkedMap['driveList:power_sunroof:6'] ?? false} onChange={() => toggleCheckbox('driveList', 'power_sunroof', 6)} />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                        </div>
                                                        <div className="col-12 content-child-div">
                                                            <label className="check-label d-inline">
                                                                <span style={{ fontWeight: 'bold' }}>里程數</span>
                                                                <input type="checkbox" className="checkbox-form" checked={checkedMap['driveList:odometer:7'] ?? false} onChange={() => toggleCheckbox('driveList', 'odometer', 7)} />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                            <input className="input-text car-milage" id="car_mileage" type="number" value={formFields.carMileage} onChange={e => handleTextChange('carMileage', e.target.value)} style={{ width: '50%' }} />
                                                            <span style={{ fontSize: 14 }}>公里</span>
                                                        </div>
                                                        <div className="col-12 content-child-div">
                                                            <label className="check-label mr-3">
                                                                <span style={{ fontWeight: 'bold' }}>隨車電子車主手冊</span><br />
                                                                <input type="checkbox" className="checkbox-form" checked={checkedMap['driveList:digital_owner_manual:8'] ?? false} onChange={() => toggleCheckbox('driveList', 'digital_owner_manual', 8)} />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/** 其他 */}
                                        <div className="col-12 text-center bg-dark py-1" style={{ borderRight: '1px solid #FFF' }}>
                                            <span className="text-white">其他</span>
                                        </div>
                                        <div className="col-12 my-2 px-0">
                                            <div className="row content-div other-div">
                                                <div className="col-12 content-child-div">
                                                    <label className="check-label mr-3">
                                                        <strong>PAII 智能駕駛輔助系統，並非全自動駕駛，仍需手握方向盤操作；在高速行駛情況下，會忽略靜止車輛，請詳閱隨車電子車主手冊。</strong>
                                                        <input type="checkbox" className="checkbox-form" checked={checkedMap['other:pilot_assist_notice:0'] ?? false} onChange={() => toggleCheckbox('other', 'pilot_assist_notice', 0)} />
                                                        <span className="checkmark"></span>
                                                    </label>
                                                </div>
                                                <div className="col-12 content-child-div">
                                                    <label className="check-label mr-3">SRS輔助安全系統的作用原理說明並告知應配合安全帶使用方能達到安全目的。
                                                        <input type="checkbox" className="checkbox-form" checked={checkedMap['other:srs_system_notice:1'] ?? false} onChange={() => toggleCheckbox('other', 'srs_system_notice', 1)} />
                                                        <span className="checkmark"></span>
                                                    </label>
                                                </div>
                                                <div className="col-12 content-child-div">
                                                    <label className="check-label mr-3">可調式氣壓式懸吊控制系統附 Four-C 操作說明。
                                                        <input type="checkbox" className="checkbox-form" checked={checkedMap['other:four_c_suspension_intro:2'] ?? false} onChange={() => toggleCheckbox('other', 'four_c_suspension_intro', 2)} />
                                                        <span className="checkmark"></span>
                                                    </label>
                                                </div>
                                                <div className="col-12 content-child-div">
                                                    <label className="check-label mr-3">提供最新版充電網路服務說明紙本
                                                        <span className="note-span"> ( 彩色列印 ) ( Recharge 車款 )。</span>
                                                        <input type="checkbox" className="checkbox-form" checked={checkedMap['other:charging_network_service_doc:3'] ?? false} onChange={() => toggleCheckbox('other', 'charging_network_service_doc', 3)} />
                                                        <span className="checkmark"></span>
                                                    </label>
                                                </div>
                                                <div className="col-12 content-child-div">
                                                    <label className="check-label mr-3">車輛充電操作說明
                                                        <span className="note-span"> ( Recharge 車款 )。</span>
                                                        <input type="checkbox" className="checkbox-form" checked={checkedMap['other:vehicle_charging_intro:4'] ?? false} onChange={() => toggleCheckbox('other', 'vehicle_charging_intro', 4)} />
                                                        <span className="checkmark"></span>
                                                    </label>
                                                </div>
                                                <div className="col-12 content-child-div">
                                                    <label className="check-label mr-3">說明 VOLVO 家用充電樁安裝注意事項
                                                        <span className="note-span"> ( Recharge 車款 )。</span>
                                                        <input type="checkbox" className="checkbox-form" checked={checkedMap['other:home_charger_installation_notice:5'] ?? false} onChange={() => toggleCheckbox('other', 'home_charger_installation_notice', 5)} />
                                                        <span className="checkmark"></span>
                                                    </label>
                                                </div>
                                                <div className="col-12 content-child-div">
                                                    <label className="check-label mr-3">交車時銷售顧問陪同客戶試駕。
                                                        <input type="checkbox" className="checkbox-form" checked={checkedMap['other:test_drive_with_sales:6'] ?? false} onChange={() => toggleCheckbox('other', 'test_drive_with_sales', 6)} />
                                                        <span className="checkmark"></span>
                                                    </label>
                                                </div>
                                                <div className="col-12 content-child-div">
                                                    <label className="check-label mr-3">說明 Volvo Cars 直接提供之限時數位服務訂閱
                                                        <input type="checkbox" className="checkbox-form" checked={checkedMap['other:digital_service_subscription_intro:7'] ?? false} onChange={() => toggleCheckbox('other', 'digital_service_subscription_intro', 7)} />
                                                        <span className="checkmark"></span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/** 簽名區域 */}
                                    <section className="signature-section">
                                        <div className="signature-panel">
                                            <div className="signature-row">
                                                <div className="signature-label">銷售顧問簽章：</div>
                                                <div id="sign-box-sales" className="signature-box sign-box-div" onClick={() => setActiveSignature('sales')}>
                                                    {signatures.sales.image ? <img src={signatures.sales.image} className="signature-image" alt="sales-signature" /> : <span className="sign print_none">請點我簽名</span>}
                                                </div>
                                            </div>
                                            <div className="signature-row signature-date-row">
                                                <div className="signature-date">日期：</div>
                                                <div className="signature-box-spacer signature-date-value">
                                                    <span id="sign-box-sales-date">{signatures.sales.date}</span></div>
                                            </div>
                                        </div>
                                        <div className="signature-panel">
                                            <div className="signature-row">
                                                <div className="signature-label">顧客簽章：</div>
                                                <div id="sign-box-customer" className="signature-box sign-box-div" onClick={() => setActiveSignature('customer')}>
                                                    {signatures.customer.image ? <img src={signatures.customer.image} className="signature-image" alt="customer-signature" /> : <span className="sign print_none">請點我簽名</span>}
                                                </div>
                                            </div>
                                            <div className="signature-row signature-date-row">
                                                <div className="signature-date">日期：</div>
                                                <div className="signature-box-spacer signature-date-value">
                                                    <span id="sign-box-customer-date">{signatures.customer.date}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </section>
                                    <button id="btn_sign_submit" className="print_none btn" type="button" disabled={isSubmitting} onClick={handleSubmit}>
                                        儲存PDF
                                    </button>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        {/** 簽名modal */}
        {activeSignature ? (
            <div className="car-delivery-sign-modal print_none">
                <div className="car-delivery-sign-dialog">
                    <div className="car-delivery-sign-title">{activeSignature === 'sales' ? '銷售顧問簽章' : '顧客簽章'}</div>
                    <canvas
                        ref={canvasRef}
                        className="car-delivery-sign-canvas"
                        onPointerDown={handleSignStart}
                        onPointerMove={handleSignMove}
                        onPointerUp={handleSignEnd}
                        onPointerLeave={handleSignEnd}
                    />
                    <div className="car-delivery-sign-actions">
                        <button type="button" className="car-delivery-sign-button" onClick={() => setActiveSignature(null)}>取消</button>
                        <button type="button" className="car-delivery-sign-button" onClick={clearSignatureCanvas}>清除</button>
                        <button type="button" className="car-delivery-sign-button primary" onClick={saveSignature}>儲存</button>
                    </div>
                </div>
            </div>
        ) : null}

    </React.Fragment>
};

export default CarDeliveryConfirmForm;